import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoCloseCircleOutline } from 'react-icons/io5';
import FareCard from './FareCard';
import CarouselArrows from '../ui/CarouselArrows';
import fareCardsData from '../../data/fareCards.json';

const FareDrawer = ({ isOpen, onClose, flightData, fareType, onSelectFare, tripType = 'roundTrip' }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedRefundOptions, setSelectedRefundOptions] = useState({});
  const drawerRef = useRef(null);

  // Reset currentIndex when fare options change
  useEffect(() => {
    setCurrentIndex(0);
  }, [fareType, flightData]);

           // Scroll drawer into view when it opens
    useEffect(() => {
      if (isOpen && drawerRef.current) {
        // Add a small delay to ensure the drawer is rendered
        setTimeout(() => {
          // Get the flight card element (parent of the drawer)
          const flightCard = drawerRef.current.previousElementSibling;
          if (flightCard) {
            // Scroll to show the flight card with a small offset to prevent cutting
            const headerHeight = 70; // Height of the sticky header
            const offset = 1; // Additional padding
            const elementTop = flightCard.offsetTop;
            const scrollTop = elementTop - headerHeight - offset;
            
            window.scrollTo({
              top: scrollTop,
              behavior: 'smooth'
            });
          } else {
            // Fallback to drawer if flight card not found
            drawerRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest'
            });
          }
        }, 100);
      }
    }, [isOpen]);

  if (!isOpen) return null;

     // Get fare options from JSON data based on fare type
     const getFareOptions = () => {
    if (!flightData) return [];

    if (fareType === 'main') {
      return getRandomizedMainFares();
    } else if (fareType === 'premiumEconomy') {
      // Premium Economy fare options - show Premium Economy and Business
      if (!flightData.premiumEconomyPrice) return [];
      return getPremiumEconomyFares();
    } else {
      // Premium fare options
      if (!flightData.premiumPrice) return [];
      return getRandomizedPremiumFares();
    }
  };

  // Get main fares in specific sequence: Basic Economy → Main → Main Plus → Main Select → First
  const getRandomizedMainFares = () => {
    const allMainFares = fareCardsData.mainFares;
    const flightSeed = flightData.id || 1;
    
    // Define the exact sequence for main fares
    const mainFareSequence = [
      'Basic Economy',
      'Main',
      'Main Plus', 
      'Main Select',
      'First'
    ];
    
    // Get fares in the specific order
    const orderedFares = [];
    mainFareSequence.forEach(fareType => {
      const fare = allMainFares.find(f => f.type === fareType);
      if (fare) {
        orderedFares.push(fare);
      }
    });
    
    // Return 3-5 fares based on flight ID (but in sequence order)
    const fareCount = 3 + (flightSeed % 3); // 3, 4, or 5 fares
    return orderedFares.slice(0, Math.min(fareCount, orderedFares.length));
  };

  // Get Premium Economy fares - show Premium Economy and Business
  const getPremiumEconomyFares = () => {
    const allPremiumEconomyFares = fareCardsData.premiumEconomyFares;
    // Always show Premium Economy and Business in that order
    return allPremiumEconomyFares.filter(fare => 
      fare.type === 'Premium Economy' || fare.type === 'Business'
    );
  };

  // Get premium fares based on route type
  const getRandomizedPremiumFares = () => {
    const allPremiumFares = fareCardsData.premiumFares;
    
    if (flightData.routeType === 'international') {
      // International flights: Show only First
      const firstFare = allPremiumFares.find(fare => fare.type === 'First');
      return firstFare ? [firstFare] : [];
    } else {
      // Domestic flights: Show First and sometimes Business
      const flightSeed = flightData.id || 1;
      const firstFare = allPremiumFares.find(fare => fare.type === 'First');
      const businessFare = allPremiumFares.find(fare => fare.type === 'Business');
      
      const availableFares = [];
      if (firstFare) availableFares.push(firstFare);
      
      // Sometimes add Business for domestic flights
      if (businessFare && (flightSeed % 2 === 0)) { // 50% chance
        availableFares.push(businessFare);
      }
      
      return availableFares;
    }
  };

           const fareOptions = getFareOptions();

   const itemsPerView = 3;
   const maxIndex = Math.max(0, fareOptions.length - itemsPerView);
   const showNavigation = fareOptions.length > itemsPerView;

  const handlePrevious = () => {
    setCurrentIndex(Math.max(0, currentIndex - 1));
  };

  const handleNext = () => {
    setCurrentIndex(Math.min(maxIndex, currentIndex + 1));
  };

  const handleRefundOptionChange = (fareId, optionId) => {
    setSelectedRefundOptions({
      ...selectedRefundOptions,
      [fareId]: optionId
    });
  };

  const handleSelectFare = (fare) => {
    console.log('Selected fare:', fare);
    // Close the drawer and trigger parent component to show flight details
    if (onSelectFare) {
      onSelectFare(fare, flightData);
    }
    onClose();
  };


  return (
    <div 
      ref={drawerRef}
      className="border-t-2 border-[#0078d2] shadow-lg" 
      style={{ backgroundColor: '#ecf4fe' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-medium text-gray-800">Select one of these fares</h2>
        </div>
        
                 <div className="flex items-center gap-4">
           {/* Navigation arrows - only show when there are more than 3 cards */}
           <CarouselArrows
             onPrevious={handlePrevious}
             onNext={handleNext}
             canGoPrevious={currentIndex > 0}
             canGoNext={currentIndex < maxIndex}
             show={showNavigation}
           />
          
                     {/* Close button */}
           <button
             onClick={onClose}
             className="text-[#0078d2] p-2 transition-all relative group flex items-center justify-center"
             title="Close drawer"
           >
             <IoCloseCircleOutline size={24} />
             <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-6 h-0.5 bg-[#0078d2] scale-x-0 group-hover:scale-x-100 transition-transform duration-200"></div>
           </button>
        </div>
      </div>

      {/* Fare Cards Container */}
      <div className="px-6 pb-6 overflow-hidden">
        <div className="relative">
          <div 
            className="flex gap-4 transition-transform duration-300 ease-in-out"
            style={{ 
              transform: showNavigation ? `translateX(-${currentIndex * 320}px)` : 'translateX(0)',
              width: showNavigation ? `${fareOptions.length * 320}px` : '100%'
            }}
          >
            {fareOptions.map((fare, index) => (
              <FareCard
                key={fare.id}
                fare={fare}
                onRefundChange={handleRefundOptionChange}
                selectedRefundOption={selectedRefundOptions[fare.id]}
                onSelect={handleSelectFare}
                flightData={flightData}
                tripType={tripType}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-white border-t border-gray-200" style={{ backgroundColor: '#ecf4fe' }}>
        <div className="text-xs text-gray-600 space-y-2">
          <p>If you're an AAdvantage® status member or AAdvantage® credit cardholder, your bag benefit and boarding group applies if higher based on status. Benefits may not apply on partner airlines.</p>
          <p>If you're an AAdvantage® credit cardholder, your bag benefit applies only on domestic American Airlines marketed and operated trips.</p>
          <p>If you're an AAdvantage® member and a U.S. resident booked on American Airlines operated flights starting within the U.S., you may be able to cancel Basic Economy tickets for travel credit before your trip begins, minus a cancellation fee.</p>
          <p>Changes for trips that begin in Spain, the UK, New Zealand or Australia are allowed with a fee.</p>
        </div>
      </div>
    </div>
  );
};

export default FareDrawer;